import React from 'react';
import { usdToTtd } from '../../utils/fxDisplay';
import { currencyPrefix } from '../../utils/priceUtils';

// Moved out of QuotationDetailPage.tsx unchanged (2026-09-26) so the Pricing
// Summary's absorbed-charges rows (`AbsorbedChargesRows`) can use the same cell.
/**
 * The TT$ half of a Pricing Summary row.
 *
 * Both summary boxes now state every money row in US$ and in TT$ side by side:
 * USC buys and pays in Trinidad dollars, so reading a quote meant converting the
 * whole box in your head. ONE component rather than sixteen more hand-written
 * cells — the two boxes already carry near-identical copies of every row, and a
 * third column written out longhand is exactly where they would start to disagree.
 *
 * `usdToTtd` does the only conversion (BUSINESS_RULES#usd-cost-and-ttd-display), so a missing rate
 * shows a dash instead of restating the US$ number as though it were TT$. The rows
 * holding a PERCENTAGE rather than money pass `usd={null}` and get the same dash:
 * a percentage is currency-free and has no TT$ statement to make.
 */
export const SummaryTtdCell: React.FC<{
  usd: number | null | undefined;
  ttdToUsd: number | null;
  /**
   * The TT$ for a row that has a better answer than converting its US$ — the
   * same escape hatch the header chips' `ttd` key is (USC-BUG-0022). Profit
   * passes `resolveProfitTtd(...).profit`, so it is the TT$ selling less the TT$ cost
   * the cards beside it show, not a second, independently-converted figure.
   */
  ttd?: number | null;
  /**
   * A percentage computed from the TT$ figures (`resolveProfitTtd`): shown after
   * the money on a money row, or alone on a percentage row (`usd={null}`).
   */
  percent?: number | null;
  /** Top row of a box: it carries the border the rows below get from their own top. */
  first?: boolean;
  rounded?: 'tr' | 'br';
  className?: string;
  title?: string;
}> = ({ usd, ttdToUsd, ttd: ttdOverride, percent, first = false, rounded, className = 'text-slate-900', title }) => {
  const ttd = ttdOverride !== undefined ? ttdOverride : usdToTtd(usd, ttdToUsd);
  const pct = percent != null && Number.isFinite(percent) ? `${percent.toFixed(1)}%` : null;
  const text = ttd != null ? `${currencyPrefix('TTD')}${ttd.toFixed(2)}${pct ? ` (${pct})` : ''}` : pct ?? '—';
  return (
    <div
      className={`flex justify-center items-center w-[148px] flex-shrink-0 min-w-0 overflow-hidden bg-white ${
        rounded === 'tr' ? 'rounded-tr-md' : rounded === 'br' ? 'rounded-br-md' : ''
      }`}
      style={{
        padding: '4px 8px',
        borderWidth: first ? '1px 1px 0 0' : '0 1px 1px 0',
        borderStyle: 'solid',
        borderColor: '#D7DBD9',
      }}
    >
      {/* The value leads the tooltip, so a figure the cell truncates is still readable. */}
      <span className={`font-['Public_Sans'] text-xs truncate ${className}`} title={title && text !== '—' ? `${text} — ${title}` : title ?? text}>
        {text}
      </span>
    </div>
  );
};
